import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  Play,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { MobilePageWrapper } from '../../../components/layout/MobilePageWrapper';
import { toMinutes, turmasComAlunosSugeridas } from '../../../components/chamada';
import type { AulaStatus } from '../../../components/chamada';
import type { HorarioAula, Turma } from '../../../types/models';
import { formatDateSafe, parseDateSafe } from './chamada-professor-shared';

type StatusMapEntry = {
  status: string;
  totalPresentes: number;
  totalAusentes: number;
  totalAlunos?: number;
};

export type ChamadaBriefingMobileProps = {
  dataAtual: string;
  setDataAtual?: (d: string) => void;
  hojeStr: string;
  aulasHoje: HorarioAula[];
  turmas: Turma[];
  statusMap: Record<string, StatusMapEntry>;
  aulaAgora: HorarioAula | null;
  aulaJaFeita: boolean;
  chamadaId: string | null;
  loadingAlunos?: boolean;
  iniciandoChamada?: boolean;
  contagemPorTurma?: Record<string, number>;
  todosAlunos?: Array<{
    turmaId?: string | null;
    ativo?: boolean;
    historicoTurmas?: Array<{ turmaId: string; atual?: boolean }>;
  }>;
  totalPerdidas: number;
  getAulaStatus: (slot: HorarioAula) => AulaStatus;
  countAlunosTurma: (turmaId: string) => number;
  agoraOperacional: () => Date;
  onRequestStart: (aula: HorarioAula) => void;
  onContinue: () => void;
  openAulaDetail: (slot: HorarioAula) => void;
  setDialogPerdida: (h: HorarioAula) => void;
  setFaseAtual: (f: 'briefing' | 'operacao' | 'resumo') => void;
};

export function ChamadaBriefingMobile({
  dataAtual,
  setDataAtual,
  hojeStr,
  aulasHoje,
  turmas,
  statusMap,
  aulaAgora,
  aulaJaFeita,
  chamadaId,
  loadingAlunos,
  iniciandoChamada,
  contagemPorTurma,
  todosAlunos = [],
  totalPerdidas,
  getAulaStatus,
  countAlunosTurma,
  agoraOperacional,
  onRequestStart,
  onContinue,
  openAulaDetail,
  setDialogPerdida,
}: ChamadaBriefingMobileProps) {
  const validAulas = aulasHoje.filter((a) => getAulaStatus(a) !== 'vazia');
  const feitas = validAulas.filter((a) => getAulaStatus(a) === 'feita').length;

  return (
    <MobilePageWrapper
      title="Chamada"
      icon={ClipboardList}
      iconColor="#fff"
      hideBackButton
      badge={validAulas.length ? `${feitas}/${validAulas.length}` : undefined}
      hasPendencias={totalPerdidas > 0}
      mobileOnly
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'capitalize',
          }}
        >
          {formatDateSafe(dataAtual, { weekday: 'long', day: 'numeric', month: 'long' })}
          {validAulas.length > 0 && (
            <span style={{ fontWeight: 500 }}>
              {' · '}
              {feitas} de {validAulas.length} concluídas
            </span>
          )}
        </div>

        {iniciandoChamada && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 12,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e40af',
              fontSize: '0.78rem',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            Preparando chamada…
          </div>
        )}

        <div
          style={{
            background: 'var(--bg-card, white)',
            borderRadius: 14,
            padding: '10px 8px',
            border: '1px solid var(--border-color, #e5e7eb)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-around', gap: 4 }}>
            {(() => {
              const baseDate = parseDateSafe(dataAtual);
              const startOfWeek = new Date(baseDate);
              const dayOfWeek = baseDate.getDay();
              const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
              startOfWeek.setDate(baseDate.getDate() + diffToMon);
              const dNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];
              const refHoje =
                hojeStr && /^\d{4}-\d{2}-\d{2}$/.test(hojeStr) ? hojeStr : dataAtual;

              return Array.from({ length: 5 }, (_, i) => {
                const d = new Date(startOfWeek);
                d.setDate(startOfWeek.getDate() + i);
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                const dateStr = `${yyyy}-${mm}-${dd}`;
                const isSelected = dateStr === dataAtual;
                const isToday = dateStr === refHoje;
                const isPast = !!refHoje && dateStr < refHoje;

                let dayBg = 'transparent';
                let dayColor = 'var(--text-main, #1e293b)';
                let borderStyle = 'none';
                let shadowStyle = 'none';
                let fontWeightStyle: number = 500;

                if (isSelected) {
                  dayBg = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                  dayColor = '#fff';
                  shadowStyle = '0 2px 6px rgba(37, 99, 235, 0.25)';
                  fontWeightStyle = 700;
                } else if (isToday) {
                  dayBg = 'rgba(37, 99, 235, 0.08)';
                  borderStyle = '1px solid rgba(37, 99, 235, 0.2)';
                  dayColor = '#2563eb';
                  fontWeightStyle = 600;
                } else if (isPast) {
                  dayColor = 'var(--text-muted, #9ca3af)';
                }

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setDataAtual?.(dateStr)}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '6px 2px',
                      borderRadius: 10,
                      background: dayBg,
                      color: dayColor,
                      border: borderStyle,
                      boxShadow: shadowStyle,
                      cursor: 'pointer',
                      fontWeight: fontWeightStyle,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.55rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        opacity: isSelected ? 0.8 : 0.6,
                        marginBottom: 2,
                      }}
                    >
                      {dNames[i]}
                    </div>
                    <div style={{ fontSize: '0.85rem' }}>{d.getDate()}</div>
                  </button>
                );
              });
            })()}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--text-muted, #94a3b8)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              paddingLeft: 4,
            }}
          >
            Aulas de hoje
          </div>

          {aulasHoje.length === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                background: 'var(--bg-card, white)',
                borderRadius: 14,
                border: '1px solid var(--border-color, #e5e7eb)',
                color: 'var(--text-muted, #94a3b8)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Clock size={24} style={{ opacity: 0.4 }} />
              <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                Nenhuma aula agendada para hoje
              </div>
            </div>
          ) : (
            aulasHoje.map((h) => {
              const status = getAulaStatus(h);
              const turma = turmas.find((t) => t.id === h.turmaId);
              const stats = statusMap[h.id];
              const isActive = aulaAgora?.id === h.id;
              const qtdAlunos = countAlunosTurma(h.turmaId);
              const isVazia = !loadingAlunos && qtdAlunos === 0;
              const sugestoesVazia =
                isVazia && !contagemPorTurma
                  ? turmasComAlunosSugeridas(turma, turmas, todosAlunos)
                  : [];

              let cardBg = 'var(--bg-card, white)';
              let cardBorder = '1px solid var(--border-color, #e5e7eb)';
              let iconBg = 'rgba(148, 163, 184, 0.1)';
              let iconColor = '#64748b';
              let iconEl = <Clock size={16} />;
              let actionText = 'Iniciar';
              let actionColor = '#64748b';

              if (status === 'feita') {
                cardBg = '#f0fdf4';
                cardBorder = '1px solid #bbf7d0';
                iconBg = 'rgba(22, 163, 74, 0.1)';
                iconColor = '#16a34a';
                iconEl = <CheckCircle2 size={16} />;
                actionText = 'Ver';
                actionColor = '#16a34a';
              } else if (isVazia) {
                cardBg = '#f8fafc';
                cardBorder = '1px solid #f1f5f9';
                iconBg = 'rgba(148, 163, 184, 0.1)';
                iconColor = '#94a3b8';
                iconEl = <Users size={16} />;
                actionText = '0 Alunos';
                actionColor = '#94a3b8';
              } else if (status === 'perdida') {
                cardBg = '#fef2f2';
                cardBorder = '1px solid #fecaca';
                iconBg = 'rgba(220, 38, 38, 0.1)';
                iconColor = '#dc2626';
                iconEl = <AlertTriangle size={16} />;
                actionText = 'Fazer';
                actionColor = '#dc2626';
              } else if (isActive && !aulaJaFeita) {
                cardBg = '#eff6ff';
                cardBorder = '1.5px solid #bfdbfe';
                iconBg = 'rgba(37, 99, 235, 0.1)';
                iconColor = '#2563eb';
                iconEl = <Play size={16} />;
                actionText = chamadaId ? 'Continuar' : 'Iniciar';
                actionColor = '#2563eb';
              }

              return (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => {
                    if (isVazia && status !== 'feita') return;
                    if (status === 'perdida') {
                      setDialogPerdida(h);
                      return;
                    }
                    if (status === 'feita') {
                      openAulaDetail(h);
                      return;
                    }
                    if (isActive && !aulaJaFeita) {
                      if (iniciandoChamada) return;
                      if (chamadaId) onContinue();
                      else onRequestStart(h);
                      return;
                    }
                    if (status === 'proxima') {
                      if (iniciandoChamada) return;
                      const agora = agoraOperacional();
                      const nowStr = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
                      const minAtual = toMinutes(nowStr);
                      const minInicio = h.horaInicio ? toMinutes(h.horaInicio) : 999;
                      const faltam = minInicio - minAtual;
                      if (faltam <= 5) onRequestStart(h);
                      else
                        toast.info(
                          `Aguarde. A aula inicia às ${h.horaInicio} (faltam ${faltam} min).`,
                        );
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: cardBg,
                    border: cardBorder,
                    cursor:
                      isVazia && status !== 'feita'
                        ? 'not-allowed'
                        : status === 'perdida' ||
                            status === 'feita' ||
                            status === 'proxima' ||
                            (isActive && !aulaJaFeita)
                          ? 'pointer'
                          : 'default',
                    textAlign: 'left',
                    width: '100%',
                    opacity: isVazia && status !== 'feita' ? 0.65 : 1,
                    boxShadow:
                      isActive && !isVazia
                        ? '0 4px 12px rgba(37, 99, 235, 0.08)'
                        : '0 1px 3px rgba(0,0,0,0.01)',
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: iconBg,
                      color: iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {iconEl}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: 'var(--text-main, #1e293b)',
                      }}
                    >
                      {h.materia?.nome || `${h.numeroAula}ª Aula`}
                    </div>
                    <div
                      style={{
                        fontSize: '0.72rem',
                        color: 'var(--text-muted, #6b7280)',
                        marginTop: 3,
                      }}
                    >
                      {turma?.nome || ''} · {h.horaInicio} - {h.horaFim}
                      {!loadingAlunos && ` · ${qtdAlunos} aluno${qtdAlunos !== 1 ? 's' : ''}`}
                    </div>
                    {isVazia && sugestoesVazia.length > 0 && (
                      <div
                        style={{
                          fontSize: '0.65rem',
                          color: '#b45309',
                          marginTop: 4,
                        }}
                      >
                        Alunos em:{' '}
                        {sugestoesVazia.map((s) => `${s.nome} (${s.total})`).join(' · ')}
                      </div>
                    )}
                    {status === 'feita' && stats && (
                      <div
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          color: '#16a34a',
                          marginTop: 4,
                        }}
                      >
                        {stats.totalPresentes} Presentes · {stats.totalAusentes} Faltas
                      </div>
                    )}
                  </div>
                  {((status === 'perdida' ||
                    status === 'feita' ||
                    (isActive && !aulaJaFeita)) &&
                    !(isVazia && status !== 'feita')) && (
                    <div
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: actionColor,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexShrink: 0,
                      }}
                    >
                      <span>{actionText}</span>
                      <ChevronRight size={14} />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </MobilePageWrapper>
  );
}
